import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faExclamationTriangle, faLightbulb, faRocket, faArrowRight,
    faDesktop, faServer, faDatabase, faChartBar, faRandom,
    faShip, faClock, faLink
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

import vis from "../../assets/images/use-cases/PortContainer-Dashboard.gif";
import pyramid from "../../assets/images/use-cases/PortContainer-Process.png";

const PortContainerVisibility = () => {
    return (
        <>
            <SEO
                title="Port Container Visibility: AI Digital Twin for Logistics | Nebulyx AI"
                description="Model your port ecosystem as connected shipping lines, terminals, customs, and transport systems. Simulate container handoffs to find visibility gaps and rebuild the digital thread."
                keywords="AI digital twin, port visibility, container tracking, supply chain, logistics, demurrage, customs clearance, digital thread"
                canonicalUrl="https://nebulyx.ai/use-cases/port-container-visibility"
                ogType="article"
            />

            <article className="use-case-page">
                {/* Hero */}
                <header className="use-case-hero">
                    <Container>
                        <Row>
                            <Col className={"w-100"}>
                                <div className={"w-100 text-center"}>
                                    <div >
                                        <Badge className="sector-badge m-1 mb-4 h-100">
                                            <FontAwesomeIcon icon={faShip}/>
                                            &nbsp;
                                            Logistics
                                        </Badge>
                                        <h1 className="use-case-title">Question:&nbsp;Where does the data thread snap between ship and shore?</h1>
                                    </div>
                                </div>
                                <div>
                                    <p className="use-case-subtitle w-100 text-center">
                                        Model your port ecosystem as connected shipping lines, terminals, customs, and transport systems. Simulate container handoffs to find visibility gaps, before they cause delays, demurrage, and lost productivity.
                                    </p>
                                </div>
                            </Col>
                        </Row>

                        <Row className="hero-stats-inline ">
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faLink} className="stat-icon"/>
                                <span className="stat-value">94%</span>
                                <span className="stat-label">Visibility Score</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faClock} className="stat-icon"/>
                                <span className="stat-value">-60%</span>
                                <span className="stat-label">Delay Reduction</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faChartLine} className="stat-icon"/>
                                <span className="stat-value">£1.2M</span>
                                <span className="stat-label">Demurrage Saved</span>
                            </Col>
                        </Row>

                    </Container>
                </header>

                {/* Executive Summary */}
                <section className="exec-summary-section">
                    <Container>
                        <h2 className="section-title">Executive Summary</h2>
                        <Row className="g-4">
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span className={"card-icon-wrapper problem me-1"}><FontAwesomeIcon
                                            sty icon={faExclamationTriangle}/></span>The Problem</h3>
                                        <ul className="summary-list">
                                            <li>Data thread snaps at every handoff</li>
                                            <li>Systems don't speak the same language</li>
                                            <li>Single missed event derails planning</li>
                                            <li>Demurrage costs spiral silently</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper solution me-1"}><FontAwesomeIcon
                                            sty icon={faLightbulb}/></span>The Solution</h3>
                                        <ul className="summary-list">
                                            <li>Build a digital twin of port ecosystem</li>
                                            <li>Model container journey end-to-end</li>
                                            <li>Overlay visibility gap detection</li>
                                            <li>Simulate disruptions before they hit</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper benefits me-1"}><FontAwesomeIcon
                                            sty icon={faRocket}/></span>The Benefits</h3>
                                        <ul className="summary-list">
                                            <li>One version of the truth</li>
                                            <li>Predict dwell time accurately</li>
                                            <li>Proactive exception management</li>
                                            <li>Slash demurrage and detention</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* Process Steps */}
                <section className="content-section process-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin Process</h2>
                        <p className="text-center text-muted mb-4">
                            Connect disparate port systems, model container journeys as a flow network, overlay visibility analytics, then simulate disruptions to find where the digital thread breaks.
                        </p>
                        <div className="process-steps-container">
                            <div className="process-step-item">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <strong>Connect Data</strong>
                                    <span>AIS/vessel tracking, TOS, customs systems, transport booking</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <strong>Model the Journey</strong>
                                    <span>Map vessel → berth → customs → yard → gate → destination</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <strong>Overlay Analytics</strong>
                                    <span>Visibility scoring, dwell prediction, handoff risk detection</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <strong>Scenario Analysis</strong>
                                    <span>Simulate vessel delays, customs holds, system outages</span>
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* AI Digital Twin - Two Sections */}
                <section className="content-section twin-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin</h2>
                        <Row className="g-4">
                            {/* Back-end Architecture */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon backend">
                                                <FontAwesomeIcon icon={faServer}/>
                                            </div>
                                            <h3>Digital Twin Process Flow</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Ingests event streams from vessel tracking, terminal operating systems, customs platforms, and transport providers.
                                            Entity resolution links container IDs across systems despite format mismatches, while visibility gap detection identifies
                                            exactly where the digital thread breaks, enabling proactive intervention before delays cascade.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={pyramid} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Front-end UI */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon frontend">
                                                <FontAwesomeIcon icon={faDesktop}/>
                                            </div>
                                            <h3>Port Control Tower</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Visualises containers on an interactive port map showing real-time positions from vessel to gate.
                                            Operations teams can see visibility scores per container, identify handoff gaps with warning indicators,
                                            and simulate disruptions, vessel delays, customs holds, system outages, to see cascade effects before they materialise.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={vis} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                        {/* Twin Components */}
                        <Row className="g-4 mt-4">
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faDatabase}/>
                                            </div>
                                            <h4>Data Analysed</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Vessel AIS & Port ETA</li>
                                            <li>Terminal Operating System</li>
                                            <li>Customs Declarations</li>
                                            <li>Bill of Lading Data</li>
                                            <li>Gate Appointments</li>
                                            <li>Truck/Rail Bookings</li>
                                            <li>EDI Message Streams</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faChartBar}/>
                                            </div>
                                            <h4>AI / Data Products</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Visibility score per container</li>
                                            <li>Dwell time prediction</li>
                                            <li>Handoff risk scoring</li>
                                            <li>Demurrage exposure forecast</li>
                                            <li>Customs clearance prediction</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faRandom}/>
                                            </div>
                                            <h4>Scenarios Tested</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Vessel delay +6 hours</li>
                                            <li>Customs hold / inspection</li>
                                            <li>Crane / equipment failure</li>
                                            <li>TOS system outage</li>
                                            <li>Truck appointment no-show</li>
                                            <li>Port congestion surge</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Discovery */}
                        <div className="discovery-highlight mt-5">
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <h3>The Potential Discovery</h3>
                                    <p>
                                        By modelling the port ecosystem as a digital twin and mapping every container handoff,
                                        operations could discover that 73% of visibility gaps occur at the TOS-to-customs interface due to
                                        BL number format mismatches, a fixable data quality issue causing £45k/month in demurrage.
                                        Simulation may reveal that <strong>pre-positioning customs documentation 24 hours earlier would reduce
                                        average dwell time by 18 hours</strong> and eliminate 60% of gate appointment failures, all validated
                                        before changing a single operational process.
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <div className="discovery-stat">
                                        <span className="discovery-label">Potential Strategy</span>
                                        <span className="discovery-value">-18h Dwell + £540k/yr Saved</span>
                                        <span className="discovery-note">Fix one data handoff, transform operations</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                </section>

                {/* CTA */}
                <section className="cta-section">
                    <Container>
                        <div className="cta-content">
                            <h2>Want to Rebuild Your Digital Thread?</h2>
                            <p>
                                If container visibility gaps are costing you in delays, demurrage, and lost productivity,
                                let's build your Digital Twin together.
                            </p>
                            <div className="cta-buttons">
                                <Button
                                    variant="light"
                                    size="lg"
                                    href="https://cal.eu/jon-cooke/discovery-call"
                                    className="me-3 mb-2"
                                >
                                    Book a Discovery Call
                                    <FontAwesomeIcon icon={faArrowRight} className="ms-2"/>
                                </Button>
                                <Link to="/use-cases">
                                    <Button variant="outline-light" size="lg" className="mb-2">
                                        View More Use Cases
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Container>
                </section>
            </article>
        </>
    );
};

export default PortContainerVisibility;